import { useEffect, useState } from 'react';
import { useAdminData } from '../../admin/AdminDataContext';
import { BasemapPreviewMap } from '../../admin/BasemapPreviewMap';
import type { AdminBasemap, BasemapSourceType, WmsBasemapSettings } from '../../admin/types';
import { ApiError } from '../../lib/api';
import { basemapsApi, type BasemapPreset } from '../../lib/basemapsApi';

const emptyForm: Omit<AdminBasemap, 'id'> = {
  name: '',
  sourceType: 'XYZ Tiles',
  urlTemplate: '',
  minZoom: 0,
  maxZoom: 19,
  tileSize: 256,
  crs: 'EPSG:3857',
  attribution: '',
  apiKey: '',
  enabled: true,
  isDefault: false,
};

const swatchPalette = ['bg-action-primary', 'bg-status-info', 'bg-status-success', 'bg-status-warning', 'bg-ai-accent', 'bg-text-secondary'];

export function BasemapsTab() {
  const { basemaps, basemapsLoading, createBasemap, updateBasemap, deleteBasemap } = useAdminData();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<Omit<AdminBasemap, 'id'>>(emptyForm);
  const [checkResult, setCheckResult] = useState<'idle' | 'checking' | 'ok' | 'invalid'>('idle');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminBasemap | null>(null);
  const [presets, setPresets] = useState<BasemapPreset[]>([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presetNote, setPresetNote] = useState<string | null>(null);

  useEffect(() => {
    basemapsApi.listPresets().then(setPresets).catch(() => setPresets([]));
  }, []);

  useEffect(() => {
    const found = basemaps.find((b) => b.id === selectedId);
    if (found) setForm(found);
  }, [selectedId, basemaps]);

  const filtered = basemaps.filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()));

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setCheckResult('idle');
    setSaveError(null);
    setPresetNote(null);
  };

  const handleCreateNew = () => {
    setSelectedId(null);
    setForm({ ...emptyForm, name: 'Новая подложка' });
    setCheckResult('idle');
    setSaveError(null);
    setPresetNote(null);
  };

  const handlePickPreset = (preset: BasemapPreset) => {
    setSelectedId(null);
    setForm({
      ...emptyForm,
      name: preset.name,
      sourceType: preset.sourceType,
      urlTemplate: preset.urlTemplate,
      minZoom: preset.minZoom,
      maxZoom: preset.maxZoom,
      tileSize: preset.tileSize,
      crs: preset.crs,
      attribution: preset.attribution,
      settings: preset.settings,
    });
    setCheckResult('idle');
    setSaveError(null);
    setPresetNote(preset.note);
    setPresetsOpen(false);
  };

  const handleCheckSource = () => {
    setCheckResult('checking');
    setTimeout(() => {
      const ok =
        form.sourceType === 'WMS'
          ? form.urlTemplate.trim().length > 0 && Boolean((form.settings as WmsBasemapSettings | undefined)?.layer)
          : form.urlTemplate.includes('{z}') && form.urlTemplate.includes('{x}') && form.urlTemplate.includes('{y}');
      setCheckResult(ok ? 'ok' : 'invalid');
    }, 500);
  };

  const wmsSettings = form.sourceType === 'WMS' ? (form.settings as WmsBasemapSettings | undefined) : undefined;
  const setWmsSettings = (patch: Partial<WmsBasemapSettings>) =>
    setForm({
      ...form,
      settings: { layer: '', version: '1.3.0', format: 'image/png', ...wmsSettings, ...patch },
    });

  const handleSave = async () => {
    if (!form.name.trim() || !form.urlTemplate.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = selectedId ? await updateBasemap(selectedId, form) : await createBasemap(form);
      setSelectedId(saved.id);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить подложку.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteBasemap(deleteTarget.id);
    if (selectedId === deleteTarget.id) {
      setSelectedId(null);
      setForm(emptyForm);
    }
    setDeleteTarget(null);
  };

  return (
    <div className="grid grid-cols-[500px_1fr_392px] gap-6 px-8 py-6">
      <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[15px] font-semibold text-text-primary">Каталог подложек</p>
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setPresetsOpen((v) => !v)}
                className="flex h-9 items-center rounded-[10px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary"
              >
                Из готовых ▾
              </button>
              {presetsOpen && (
                <div className="absolute right-0 top-10 z-20 w-72 rounded-xl border border-border-default bg-bg-surface p-1.5 shadow-lg">
                  {presets.length === 0 ? (
                    <p className="px-3 py-2 text-[11px] text-text-secondary">Загрузка…</p>
                  ) : (
                    presets.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => handlePickPreset(p)}
                        className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left hover:bg-bg-surface-secondary"
                      >
                        <span className="text-[13px] font-medium text-text-primary">{p.name}</span>
                        {p.note && <span className="mt-0.5 text-[10px] text-status-warning">Нужен свой URL тайлов</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleCreateNew}
              className="flex h-9 items-center rounded-[10px] bg-action-primary px-3 text-[12px] font-medium text-text-inverse hover:bg-action-primary-hover"
            >
              + Своя
            </button>
          </div>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск подложки"
          className="mt-4 h-10 rounded-[10px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
        />

        <div className="mt-4 flex flex-col gap-2">
          {basemapsLoading ? (
            <p className="py-6 text-center text-xs text-text-secondary">Загрузка…</p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-text-secondary">Ничего не найдено</p>
          ) : (
            filtered.map((b, i) => (
              <button
                key={b.id}
                type="button"
                onClick={() => handleSelect(b.id)}
                className={`flex items-center gap-4 rounded-xl border p-3 text-left transition-colors ${
                  selectedId === b.id ? 'border-action-primary bg-bg-surface-secondary' : 'border-border-default bg-bg-surface hover:bg-bg-surface-secondary'
                }`}
              >
                <span className={`flex size-[46px] shrink-0 items-center justify-center rounded-[10px] text-xs font-semibold text-text-inverse ${swatchPalette[i % swatchPalette.length]}`}>
                  {b.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-text-primary">{b.name}</span>
                  <span className="block text-[10px] text-text-secondary">
                    {b.sourceType} · {b.crs}
                  </span>
                </span>
                <span
                  className={`flex h-6 shrink-0 items-center rounded-lg px-2.5 text-[10px] font-medium ${
                    b.isDefault
                      ? 'bg-action-primary text-text-inverse'
                      : b.enabled
                        ? 'bg-status-success text-text-inverse'
                        : 'bg-bg-surface-secondary text-text-primary'
                  }`}
                >
                  {b.isDefault ? 'По умолчанию' : b.enabled ? 'Активна' : 'Отключена'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(b);
                  }}
                  title="Удалить"
                  className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-status-danger/10 hover:text-status-danger"
                >
                  ✕
                </button>
              </button>
            ))
          )}
        </div>
      </section>

      <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
        <p className="text-[13px] font-medium text-text-secondary">Предпросмотр</p>
        <div className="mt-3 h-[480px] overflow-hidden rounded-xl border border-border-default">
          <BasemapPreviewMap
            urlTemplate={form.sourceType !== 'WMTS' ? form.urlTemplate : ''}
            tileSize={form.tileSize}
            minZoom={form.minZoom}
            maxZoom={form.maxZoom}
            wms={wmsSettings}
            className="size-full"
          />
        </div>
        {form.sourceType === 'WMTS' && (
          <p className="mt-2 text-[11px] text-text-secondary">
            WMTS-подложки публикуются через мастер «Добавить слой → OGC-сервисы» — там сервер сам сообщает набор
            тайловых матриц, который здесь неоткуда взять по одному URL-шаблону.
          </p>
        )}
        {form.sourceType === 'TMS' && (
          <p className="mt-2 text-[11px] text-text-secondary">
            Для TMS используйте {'{-y}'} вместо {'{y}'} в шаблоне — строки тайлов у TMS нумеруются снизу вверх.
          </p>
        )}

        <div className="mt-4 rounded-xl border border-border-default bg-bg-surface p-4">
          <p className="text-[13px] font-semibold text-text-primary">{form.name || 'Без названия'}</p>
          <p className="mt-1 text-[10px] text-text-secondary">{form.attribution || 'Атрибуция не указана'}</p>
          <div className="mt-3 flex gap-2">
            <span className="flex h-6 items-center rounded-lg bg-bg-surface-secondary px-2.5 text-[10px] font-medium text-text-primary">
              z {form.minZoom}–{form.maxZoom}
            </span>
            <span className="flex h-6 items-center rounded-lg bg-bg-surface-secondary px-2.5 text-[10px] font-medium text-text-primary">
              {form.tileSize} px
            </span>
            <span className="flex h-6 items-center rounded-lg bg-status-info px-2.5 text-[10px] font-medium text-text-inverse">
              {form.crs}
            </span>
          </div>
        </div>
      </section>

      <aside className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
        <p className="text-[15px] font-semibold text-text-primary">{selectedId ? 'Настройки подложки' : 'Новая подложка'}</p>

        {presetNote && (
          <p className="mt-3 rounded-lg bg-status-warning/15 p-3 text-[11px] text-text-primary">{presetNote}</p>
        )}

        <label className="mt-4 text-[11px] font-medium text-text-secondary">Название</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
        />

        <label className="mt-3 text-[11px] font-medium text-text-secondary">Тип источника</label>
        <select
          value={form.sourceType}
          onChange={(e) => setForm({ ...form, sourceType: e.target.value as BasemapSourceType })}
          className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
        >
          <option>XYZ Tiles</option>
          <option>WMTS</option>
          <option>TMS</option>
          <option>MBTiles</option>
          <option>WMS</option>
        </select>

        <label className="mt-3 text-[11px] font-medium text-text-secondary">
          {form.sourceType === 'WMS' ? 'Базовый URL WMS' : 'URL шаблона'}
        </label>
        <input
          value={form.urlTemplate}
          onChange={(e) => {
            setForm({ ...form, urlTemplate: e.target.value });
            setCheckResult('idle');
          }}
          placeholder={form.sourceType === 'WMS' ? 'https://server.example.com/wms' : 'https://tile.example.com/{z}/{x}/{y}.png'}
          className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
        />
        {form.sourceType === 'WMS' ? (
          <p className="mt-1 text-[10px] text-text-secondary">
            Без параметров запроса — LAYERS/VERSION/FORMAT задаются отдельно ниже, OpenLayers сам собирает GetMap на
            каждый тайл.
          </p>
        ) : (
          <p className="mt-1 text-[10px] text-text-secondary">
            Если провайдер требует ключ прямо в URL, подставьте его в шаблон (например ...&amp;apikey=ВАШ_КЛЮЧ) — поле
            «API-ключ» ниже хранится отдельно как метаданные.
          </p>
        )}

        {form.sourceType === 'WMS' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="col-span-2 flex flex-col">
              <label className="text-[11px] font-medium text-text-secondary">Слой (LAYERS)</label>
              <input
                value={wmsSettings?.layer ?? ''}
                onChange={(e) => setWmsSettings({ layer: e.target.value })}
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] font-medium text-text-secondary">Версия WMS</label>
              <input
                value={wmsSettings?.version ?? '1.3.0'}
                onChange={(e) => setWmsSettings({ version: e.target.value })}
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] font-medium text-text-secondary">Формат</label>
              <input
                value={wmsSettings?.format ?? 'image/png'}
                onChange={(e) => setWmsSettings({ format: e.target.value })}
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              />
            </div>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-text-secondary">Минимальный zoom</label>
            <input
              type="number"
              value={form.minZoom}
              onChange={(e) => setForm({ ...form, minZoom: Number(e.target.value) })}
              className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-text-secondary">Максимальный zoom</label>
            <input
              type="number"
              value={form.maxZoom}
              onChange={(e) => setForm({ ...form, maxZoom: Number(e.target.value) })}
              className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
            />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-text-secondary">Размер тайла</label>
            <select
              value={form.tileSize}
              onChange={(e) => setForm({ ...form, tileSize: Number(e.target.value) as 256 | 512 })}
              className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
            >
              <option value={256}>256 px</option>
              <option value={512}>512 px</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-text-secondary">Система координат</label>
            <input
              value={form.crs}
              onChange={(e) => setForm({ ...form, crs: e.target.value })}
              className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
            />
          </div>
        </div>

        <label className="mt-3 text-[11px] font-medium text-text-secondary">Атрибуция</label>
        <input
          value={form.attribution}
          onChange={(e) => setForm({ ...form, attribution: e.target.value })}
          className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
        />

        <label className="mt-3 text-[11px] font-medium text-text-secondary">API-ключ / токен</label>
        <input
          value={form.apiKey ?? ''}
          onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
          placeholder="Не используется"
          className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
        />

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[11px] font-medium text-text-secondary">Доступность</span>
          <button
            type="button"
            onClick={() => setForm({ ...form, enabled: !form.enabled })}
            className={`flex h-[26px] w-[52px] items-center rounded-full px-1 transition-colors ${form.enabled ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'}`}
          >
            <span className="size-[18px] rounded-full bg-bg-surface shadow" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] font-medium text-text-secondary">Подложка по умолчанию</span>
          <button
            type="button"
            onClick={() => setForm({ ...form, isDefault: !form.isDefault })}
            className={`flex h-[26px] w-[52px] items-center rounded-full px-1 transition-colors ${form.isDefault ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'}`}
          >
            <span className="size-[18px] rounded-full bg-bg-surface shadow" />
          </button>
        </div>

        {saveError && <p className="mt-3 text-xs font-medium text-status-danger">{saveError}</p>}

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={handleCheckSource}
            className="flex h-10 flex-1 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            {checkResult === 'checking' ? 'Проверка…' : 'Проверить источник'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.urlTemplate.trim()}
            className="flex h-10 flex-1 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
        {checkResult === 'ok' && <p className="mt-2 text-[11px] font-medium text-status-success">Источник выглядит корректно</p>}
        {checkResult === 'invalid' && (
          <p className="mt-2 text-[11px] font-medium text-status-danger">
            {form.sourceType === 'WMS' ? 'Укажите URL и слой (LAYERS)' : <>URL должен содержать {'{z}/{x}/{y}'}</>}
          </p>
        )}
      </aside>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-4">
          <div className="w-full max-w-[440px] rounded-[18px] border border-border-default bg-bg-surface p-6">
            <h3 className="text-base font-semibold text-text-primary">Удалить подложку «{deleteTarget.name}»?</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Карты, использующие эту подложку по умолчанию, переключатся на первую доступную.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary"
              >
                Нет
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90"
              >
                Да, удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
